import { useAuth } from '../providers/AuthProvider';
import { usePermissions } from '@/hooks/usePermissions';
import AccessDenied from '@/features/error/pages/AccessDenied';

interface PermissionRouteProps {
    children: React.ReactNode;
    /**
     * A single permission key string or an array.
     * If an array is given, the user must have AT LEAST ONE (any).
     * Pass `requireAll` to require all of them.
     */
    permission: string | string[];
    requireAll?: boolean;
}

/**
 * Wraps a route element and renders an <AccessDenied /> page when the
 * authenticated user does not have the required permission(s).
 * Owners and super-admins bypass all checks.
 */
export default function PermissionRoute({
    children,
    permission,
    requireAll = false,
}: PermissionRouteProps) {
    const { isLoading } = useAuth();
    const { hasPermission, hasAnyPermission, hasAllPermissions, isOwner, isSuperAdmin } =
        usePermissions();

    // While auth is resolving, render nothing (ProtectedRoute already shows a spinner at the parent level)
    if (isLoading) return null;

    // Owners and super-admins always have full access
    if (isOwner || isSuperAdmin) return <>{children}</>;

    const hasAccess = Array.isArray(permission)
        ? requireAll
            ? hasAllPermissions(permission)
            : hasAnyPermission(permission)
        : hasPermission(permission);

    if (!hasAccess) {
        return <AccessDenied />;
    }

    return <>{children}</>;
}
